import area from '@turf/area';
import distance from '@turf/distance';
import { polygon as turfPolygon, point } from '@turf/helpers';

// GROUP COORDINATES FOR POLYGONS & LINES
export function groupByMapLabel(data, type) {
  const grouped = {};

  console.log('DATA ===', data);

  data
    .filter(item => item.mapType === type && item.mapLabel && item.latitude && item.longitude)
    .forEach(item => {
      const label = item.mapLabel.trim();

      if (!grouped[label]) {
        grouped[label] = {
          id: item.id,
          mapLabel: label,
          displayName: item.displayName?.trim() || label,
          hexColor: item.hexColor || '#800080',
          points: [], // store raw points with sortOrder
        };
      }

      grouped[label].points.push({
        lat: item.latitude,
        lng: item.longitude,
        sortOrder: item.sortOrder ?? 0,
      });
    });

  // 🔥 Now sort each polygon by sortOrder
  return Object.values(grouped).map(group => ({
    id: group.id,
    mapLabel: group.mapLabel,
    hexColor: group.hexColor,
    displayName: group.displayName,

    // sort by sortOrder before passing to calculateArea
    coords: group.points
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(p => ({
        lat: p.lat,
        lng: p.lng,
      })),
  }));
}

export function calculateArea(polygonsGrouped = []) {
  return polygonsGrouped
    .map(({ id, mapLabel, displayName, coords, hexColor }) => {
      if (!coords?.length) return null;

      // Convert coordinates to GeoJSON Polygon format
      const points = coords.map(c => [c.lng, c.lat]);

      // Ensure the polygon is closed
      const isClosed =
        points.length > 0 &&
        (points[0][0] !== points[points.length - 1][0] || points[0][1] !== points[points.length - 1][1]);

      if (isClosed) {
        points.push(points[0]);
      }

      const turfPoly = turfPolygon([points]);
      const areaInSqMeters = area(turfPoly);
      const areaInHectares = areaInSqMeters / 10000;

      return {
        id,
        mapLabel,
        displayName,
        coords,
        hexColor,
        area: areaInHectares.toFixed(2),
      };
    })
    .filter(Boolean);
}

export function calculatePolygonAreaGoogle(coords = []) {
  if (!coords || coords.length < 3) return '0.00';

  if (!isGoogleMapsGeometryLoaded()) {
    console.warn('Google Maps Geometry library not loaded');
    return '0.00';
  }

  // Convert to Google Maps LatLng objects
  const path = coords.map(c => new window.google.maps.LatLng(c.lat, c.lng));

  // Google computes area in square meters
  const areaSqMeters = window.google.maps.geometry.spherical.computeArea(path);

  const hectares = areaSqMeters / 10000;

  // Always return valid decimal string
  return hectares.toFixed(2);
}

export function calculateLineDistance(coords) {
  if (!coords || coords.length < 2) return '0.00';

  let total = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    const from = point([coords[i].lng, coords[i].lat]);
    const to = point([coords[i + 1].lng, coords[i + 1].lat]);
    total += distance(from, to, { units: 'kilometers' });
  }

  return total.toFixed(2); // Return distance as string in km
}

export function getBaseLabel(label = '') {
  return label.replace(/\s+\d+$/, '').trim(); // Removes trailing space + number
}

export function isGoogleMapsGeometryLoaded() {
  return (
    typeof window !== 'undefined' &&
    window.google &&
    window.google.maps &&
    window.google.maps.geometry &&
    window.google.maps.geometry.spherical
  );
}

export const getVertexRadius = zoom => {
  const metersPerPixel = 156543.03392 / Math.pow(2, zoom);
  const targetPixels = 10;
  return Math.min(30, Math.max(1, metersPerPixel * targetPixels));
};
